// frontend/src/components/TemplateComparison.jsx
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import logger from '../utils/logger';

// Helper function to strip HTML tags and decode entities for display
const stripHtmlForDisplay = (html) => {
  if (!html) return '';
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Get text content (strips all HTML tags)
  let text = tempDiv.textContent || tempDiv.innerText || '';
  
  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  text = textarea.value;
  
  return text;
};

function TemplateComparison({ template1, template2, onClose }) {
  const { token } = useContext(AuthContext);
  const [metrics1, setMetrics1] = useState(null);
  const [metrics2, setMetrics2] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (template1 && template2) {
      fetchMetrics();
    }
  }, [template1, template2]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const [res1, res2] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts/${template1._id}/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts/${template2._id}/metrics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (res1.ok) {
        const data1 = await res1.json();
        setMetrics1(data1);
      }
      if (res2.ok) {
        const data2 = await res2.json();
        setMetrics2(data2);
      }
    } catch (err) {
      logger.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const diffText = (text1, text2) => {
    // Strip HTML from both templates before comparing
    const cleanText1 = stripHtmlForDisplay(text1);
    const cleanText2 = stripHtmlForDisplay(text2);
    
    // Simple diff visualization
    const lines1 = cleanText1.split('\n');
    const lines2 = cleanText2.split('\n');
    const maxLines = Math.max(lines1.length, lines2.length);
    const diff = [];

    for (let i = 0; i < maxLines; i++) {
      const line1 = lines1[i] || '';
      const line2 = lines2[i] || '';
      
      if (line1 === line2) {
        diff.push({ type: 'same', line: line1 });
      } else {
        diff.push({ type: 'removed', line: line1 });
        diff.push({ type: 'added', line: line2 });
      }
    }

    return diff;
  };

  const templateDiff = diffText(template1?.template || '', template2?.template || '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Template Comparison</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Template Info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                {template1?.name} (v{template1?.versionNumber})
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {template1?.description || 'No description'}
              </p>
              {template1?.isActive && (
                <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                  Active
                </span>
              )}
            </div>
            <div className="p-4 border border-gray-200 dark:border-gray-700 rounded">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                {template2?.name} (v{template2?.versionNumber})
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {template2?.description || 'No description'}
              </p>
              {template2?.isActive && (
                <span className="inline-block mt-2 px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded">
                  Active
                </span>
              )}
            </div>
          </div>

          {/* Metrics Comparison */}
          {!loading && (metrics1 || metrics2) && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Performance Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded">
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Total Uses: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics1?.totalUses || 0}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Avg Response Time: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics1?.avgResponseTime ? `${metrics1.avgResponseTime.toFixed(0)}ms` : 'N/A'}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Avg Token Usage: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics1?.avgTokenUsage ? metrics1.avgTokenUsage.toFixed(0) : 'N/A'}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Error Rate: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics1?.errorRate ? `${(metrics1.errorRate * 100).toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded">
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Total Uses: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics2?.totalUses || 0}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Avg Response Time: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics2?.avgResponseTime ? `${metrics2.avgResponseTime.toFixed(0)}ms` : 'N/A'}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Avg Token Usage: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics2?.avgTokenUsage ? metrics2.avgTokenUsage.toFixed(0) : 'N/A'}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Error Rate: </span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {metrics2?.errorRate ? `${(metrics2.errorRate * 100).toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Template Diff */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Template Differences</h3>
            <div className="border border-gray-200 dark:border-gray-700 rounded p-4 bg-gray-50 dark:bg-gray-900 font-mono text-sm overflow-auto max-h-96">
              {templateDiff.map((item, index) => (
                <div
                  key={index}
                  className={`${
                    item.type === 'added'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                      : item.type === 'removed'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {item.type === 'added' && '+'}
                  {item.type === 'removed' && '-'}
                  {item.line}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateComparison;
